'use strict';

import queryString from 'query-string';
import * as affiliationTracking from '../utils/affiliationTracking';

/**
 * URL and attribution handling for the v2 loan application.
 *
 * Marketing links arrive in two shapes and both must work:
 *   /hae-yrityslainaa/loanamount=97000&length=28&capitalfreemonths=1&_ga=...
 *   /hae-yrityslainaa/?loanamount=97000&length=28
 * Path-style values win over query-string values on conflict (same rule as the
 * live flow). Loan parameters in the URL switch the page into "prefilled" mode.
 */

export const AMOUNT = { min: 2000, max: 250000, step: 1000, default: 10000 };
// Slider: the first third of the track covers 2 000–20 000 €, the rest 20 000–250 000 €
export const AMOUNT_KNEE = { value: 20000, at: 1 / 3 };
export const LENGTH = { min: 1, max: 48, default: 12 };
export const FREE_MONTHS = { min: 0, max: 3, default: 0 };

const clamp = (n, { min, max }) => Math.min(max, Math.max(min, n));

export function parseLoanParams(match, search) {
  const pathParams = queryString.parse((match && match.params && match.params.id) || '');
  const query = queryString.parse(search || '');
  const values = { ...query, ...pathParams };

  const amount = parseInt(values.loanamount, 10);
  const length = parseInt(values.length, 10);
  const free = parseInt(values.capitalfreemonths, 10);
  const prefilled = !isNaN(amount) && !isNaN(length);

  const consolidation =
    values.replace === '1' || values.consolidation === 'true' || query.replace === '1' || query.consolidation === 'true';

  return {
    values,
    mode: prefilled ? 'prefilled' : 'calculator',
    loan: {
      amount: prefilled ? Math.round(clamp(amount, AMOUNT) / AMOUNT.step) * AMOUNT.step : AMOUNT.default,
      length: prefilled ? clamp(length, LENGTH) : LENGTH.default,
      free: !isNaN(free) ? clamp(free, FREE_MONTHS) : FREE_MONTHS.default,
    },
    consolidation,
  };
}

/**
 * Campaign code from the URL (`koodi=KEVAT26`, path or query form). Letters, digits, dash and
 * underscore only, at most 30 characters — anything else is dropped so a crafted link cannot
 * push arbitrary text into the form. The user can still edit or clear the prefilled code.
 */
export function campaignCodeFromUrl(values) {
  const raw = values && values.koodi;
  if (typeof raw !== 'string') return '';
  return raw.trim().replace(/[^0-9A-Za-z\u00C0-\u017F_-]/g, '').slice(0, 30);
}

function getCookie(name) {
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : undefined;
}

const paramFromQuery = (qs, key) => {
  try { return new URLSearchParams(qs).get(key); } catch (e) { return null; }
};

/**
 * Same attribution fields the live flow posts with every application:
 * affiliate, affiliateCode, affiliateBrowserId, ga4ClientId, ga4SessionId (+ affiliateParams).
 * Sources, in priority order: gclid (Google) / fbclid (Facebook) from the URL or the
 * 90-day affiliation cookie, the `sr` param (Google Ads), the saved affiliation params,
 * and always the GA4 client/session ids and the _fbp cookie when present.
 */
export function buildAttribution(values) {
  const tracking = affiliationTracking.retrieveParams();

  let ga4ClientId = '';
  let ga4SessionId = '';
  try {
    ga4ClientId = affiliationTracking.getGA4ClientId() || '';
    const session = affiliationTracking.getGA4SessionData();
    if (session && session.sessionId) ga4SessionId = session.sessionId;
  } catch (e) { /* GA not loaded — fine */ }

  let affiliateBrowserId = affiliationTracking.getFacebookBrowserId() || '';

  let gclid = '';
  let fbclid = affiliationTracking.getFacebookClickId() || '';
  const cookie = getCookie('affiliation');
  if (cookie) {
    try {
      const saved = JSON.parse(decodeURIComponent(cookie));
      gclid = paramFromQuery(saved.query, 'gclid') || '';
      if (!fbclid) fbclid = paramFromQuery(saved.query, 'fbclid') || '';
    } catch (e) { /* malformed cookie — ignore */ }
  }

  let affiliate = values.sr !== undefined ? 'Google Ads' : '';
  let affiliateCode = values.sr || '';
  let affiliateParams;

  if (gclid) {
    affiliate = 'Google';
    affiliateCode = gclid;
    if (ga4SessionId) affiliateBrowserId = ga4SessionId; // Google traffic keys on the GA4 session
  } else if (fbclid) {
    affiliate = 'Facebook';
    affiliateCode = fbclid;
  } else if (tracking) {
    affiliate = tracking.source || affiliate;
    affiliateCode = tracking.code || affiliateCode;
    affiliateParams = tracking.query;
    affiliateBrowserId = tracking.browserId || affiliateBrowserId;
    ga4ClientId = tracking.ga4ClientId || ga4ClientId;
    ga4SessionId = tracking.ga4SessionId || ga4SessionId;
  }

  const result = { affiliate, affiliateCode, affiliateBrowserId, ga4ClientId, ga4SessionId };
  if (affiliateParams) result.affiliateParams = affiliateParams;
  return result;
}
